import clsx from "clsx";
import { type FC, useEffect, useMemo } from "react";

import { AddCourseGroupingModal } from "./AddCourseGroupingModal";
import { AddCourseModal } from "./AddCourseModal";
import { CourseNavListItem } from "./CourseNavListItem";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import {
  type SectionViewOption,
  SectionViewOptionMenu,
} from "components/menus/SectionViewOptionMenu";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { TextAddButton } from "mds/components/TextAddButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { FolderIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCourseGroupingsWithCourses,
  selectCoursesWithGrouping,
  selectUngroupedCourses,
} from "store/selectors/section";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { useQueryParamsManager } from "utils/routing";

const COURSE_EVENT_PREFIX = "Home - Course Catalog / ";

type CourseNavListProps = {
  canEdit: boolean;
  onOptionChange: (option: SectionViewOption) => void;
};

export const CourseNavList: FC<CourseNavListProps> = ({ canEdit, onOptionChange }) => {
  const groupingsWithCodes = useAppSelector(selectCourseGroupingsWithCourses);
  const ungroupedCourses = useAppSelector(selectUngroupedCourses);
  const coursesWithGrouping = useAppSelector(selectCoursesWithGrouping);
  const { setQueryParams, getQueryParam } = useQueryParamsManager();
  const hasCourses = groupingsWithCodes.length > 0 || ungroupedCourses.length > 0;
  const courseCodeId = getQueryParam(COURSE_ID_QUERY_PARAM);
  const groupingId = getQueryParam(COURSE_GROUPING_ID_QUERY_PARAM);
  const isSmallScreen = !useIsMdOrLarger();

  // Collect all course codes (from groupings and ungrouped) for finding current selection
  const allCourses = useMemo(
    () => [...groupingsWithCodes.flatMap((grouping) => grouping.courses), ...ungroupedCourses],
    [groupingsWithCodes, ungroupedCourses],
  );
  const currentGrouping = groupingsWithCodes.find((grouping) => grouping.id === groupingId);
  const currentCourse = allCourses.find((code) => code.id === courseCodeId);
  const firstGroupId = groupingsWithCodes[0]?.id;
  const firstCourseId = ungroupedCourses[0]?.id;
  const [localCourses, setLocalCourses] = useLocalCopy(allCourses);

  const moveItemsLocally = useDnDItemOrdering(localCourses, setLocalCourses);

  const onDropItem = (droppedItem: CourseType) => {
    storeApi.courses.partial_update({ id: droppedItem.id, order: droppedItem.order });
  };

  // Set default query params if neither grouping_id nor course_code_id are set
  useEffect(() => {
    if (!groupingId && !courseCodeId) {
      if (firstGroupId) {
        // Set the first grouping if there are any groupings
        setQueryParams({
          [COURSE_GROUPING_ID_QUERY_PARAM]: firstGroupId,
          [COURSE_ID_QUERY_PARAM]: null,
        });
      } else if (firstCourseId) {
        // Set the first ungrouped course if there are no groupings
        setQueryParams({
          [COURSE_ID_QUERY_PARAM]: firstCourseId,
          [COURSE_GROUPING_ID_QUERY_PARAM]: null,
        });
      }
    }
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [groupingId, courseCodeId, firstGroupId, firstCourseId, setQueryParams]);

  const handleCreateCourseCode = () => {
    showConfirmationModal({
      title: t("home.courses.add_course_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseModal
          eventName={`${COURSE_EVENT_PREFIX} Add Course Code`}
          localCourses={localCourses}
          onClose={onClose}
        />
      ),
    });
  };

  const handleCreateCourseCodeGrouping = () => {
    showConfirmationModal({
      title: t("home.courses.add_course_grouping_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseGroupingModal
          courseCodes={coursesWithGrouping}
          eventName={`${COURSE_EVENT_PREFIX} Add Course Code Grouping`}
          groupingsWithCodes={groupingsWithCodes}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div
      className={clsx(
        "grid max-h-[calc(100%-24px)] w-[240px] grid-rows-[auto_1fr_auto] rounded-2xl border border-black-tint-90 p-4",
        isSmallScreen && "w-full",
      )}
      role="list"
    >
      <div className="mb-4">
        <SectionViewOptionMenu
          eventPrefix={COURSE_EVENT_PREFIX}
          selectedOption="course"
          onOptionChange={onOptionChange}
        />
      </div>

      <div className="min-h-0 overflow-y-auto">
        {/* Render groupings with their course codes */}
        {groupingsWithCodes.map((grouping) => {
          const isSelected = currentGrouping?.id === grouping.id;
          const isExpanded = isSelected || currentCourse?.grouping_id === grouping.id;
          const localGroupingCodes = localCourses.filter(
            (code) => code.grouping_id === grouping.id,
          );

          return (
            <div key={grouping.id}>
              {/* Grouping header */}
              <div
                className="my-1 flex min-w-12 cursor-pointer items-center justify-between rounded-lg px-2 hover:bg-black-tint-97 data-[selected]:border-blue-tint-40 data-[selected]:bg-blue data-[selected]:text-white"
                data-selected={isSelected || undefined}
                onClick={() =>
                  setQueryParams({
                    [COURSE_GROUPING_ID_QUERY_PARAM]: grouping.id,
                    [COURSE_ID_QUERY_PARAM]: null,
                  })
                }
              >
                <div className="flex items-center gap-2">
                  <FolderIcon />

                  <h3 className="body-s my-1 w-full min-w-12 truncate font-medium">
                    {grouping.title}
                  </h3>
                </div>
              </div>

              {/* Course codes within grouping (indented when expanded) */}
              {isExpanded &&
                localGroupingCodes.map((course, index) => (
                  <CourseNavListItem
                    className="ml-6"
                    course={course}
                    dropIndex={index}
                    isSelected={!currentGrouping && currentCourse?.id === course.id}
                    key={course.id}
                    onDropItem={onDropItem}
                    onMoveLocally={moveItemsLocally}
                  />
                ))}
            </div>
          );
        })}

        {/* Render ungrouped course codes */}
        {ungroupedCourses.map((course, index) => {
          const localCode = localCourses.find((c) => c.id === course.id);
          if (!localCode) return null;

          return (
            <CourseNavListItem
              course={course}
              dropIndex={index}
              isSelected={!currentGrouping && currentCourse?.id === course.id}
              key={course.id}
              onDropItem={onDropItem}
              onMoveLocally={moveItemsLocally}
            />
          );
        })}
      </div>

      {canEdit && (
        <>
          <TextAddButton
            className={clsx("w-[206px] max-w-[206px]", hasCourses && "mt-2")}
            eventName={`${COURSE_EVENT_PREFIX}${t("common.add.course")}`}
            kind="gray"
            size="s"
            title={t("common.add.course")}
            onClick={() => handleCreateCourseCode()}
          >
            <span className="font-medium text-black-tint-20">{t("common.add.course")}</span>
          </TextAddButton>

          <Button
            className="mt-2 w-[206px] max-w-[206px]"
            eventName={`${COURSE_EVENT_PREFIX}${t("common.add.course_grouping")}`}
            kind="gray"
            size="s"
            title={t("common.add.course_grouping")}
            onClick={() => handleCreateCourseCodeGrouping()}
          >
            <IconText
              className="font-medium text-black-tint-20"
              iconStart={<FolderIcon />}
              text={t("common.add.course_grouping")}
            />
          </Button>
        </>
      )}
    </div>
  );
};
